import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Science as SandboxIcon,
  Verified as ProductionIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  Send as SendIcon,
  History as HistoryIcon,
  Settings as ConfigIcon,
  Description as TemplateIcon,
  Dashboard as DashboardIcon,
  Refresh as RefreshIcon,
  ErrorOutline,
  DoneAll,
  HourglassEmpty,
  OpenInNew,
  TouchApp,
  Draw as SignatureIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import {
  settingsService,
  EmailConfig,
  EmailSignature,
  EmailTemplate,
  EmailLog,
  EmailStats,
} from '../../../services';

type MainTab = 'dashboard' | 'config' | 'signatures' | 'templates' | 'history';
type Environment = 'sandbox' | 'producao';

const EmailPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<MainTab>('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dashboard data
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [recentLogs, setRecentLogs] = useState<EmailLog[]>([]);

  // Email configs
  const [configs, setConfigs] = useState<EmailConfig[]>([]);
  const [configPage, setConfigPage] = useState(0);
  const [configEnvTab, setConfigEnvTab] = useState<Environment>('producao');
  const [configDialog, setConfigDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<EmailConfig | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [configForm, setConfigForm] = useState({
    description: '',
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
    smtpPassword: '',
    fromName: '',
    fromEmail: '',
    replyTo: '',
    environment: 'producao' as Environment,
    active: true,
  });

  // Signatures
  const [signatures, setSignatures] = useState<EmailSignature[]>([]);
  const [signaturePage, setSignaturePage] = useState(0);
  const [signatureDialog, setSignatureDialog] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState<EmailSignature | null>(null);
  const [signatureForm, setSignatureForm] = useState({
    name: '',
    contentHtml: '',
    active: true,
    isDefault: false,
  });

  // Templates
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templatePage, setTemplatePage] = useState(0);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    code: '',
    name: '',
    subject: '',
    contentHtml: '',
    contentText: '',
    type: 'transacional' as EmailTemplate['type'],
    signatureId: '',
    active: true,
  });

  // Email Logs
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [logPage, setLogPage] = useState(0);
  const [logTotal, setLogTotal] = useState(0);
  const [logFilters, setLogFilters] = useState({
    status: '',
    email: '',
  });

  // Send Email dialog
  const [sendDialog, setSendDialog] = useState(false);
  const [sendForm, setSendForm] = useState({
    email: '',
    templateCode: '',
    subject: '',
    contentHtml: '',
    variables: {} as Record<string, string>,
  });
  const [sending, setSending] = useState(false);

  // Fetch all data
  const fetchDashboard = useCallback(async () => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        settingsService.getEmailStats(),
        settingsService.getEmailLogs({ limit: 5 }),
      ]);
      setStats(statsRes.data);
      setRecentLogs(logsRes.data || []);
    } catch (err: any) {
      console.error('Error fetching dashboard:', err);
    }
  }, []);

  const fetchConfigs = useCallback(async () => {
    try {
      const response = await settingsService.getEmailConfigs();
      setConfigs(response.data || []);
    } catch (err: any) {
      console.error('Error fetching configs:', err);
      setError(err.message || t('settings.email.errorLoadingConfigs'));
    }
  }, [t]);

  const fetchSignatures = useCallback(async () => {
    try {
      const response = await settingsService.getEmailSignatures();
      setSignatures(response.data || []);
    } catch (err: any) {
      console.error('Error fetching signatures:', err);
      setError(err.message || t('settings.email.errorLoadingSignatures'));
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await settingsService.getEmailTemplates();
      setTemplates(response.data || []);
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setError(err.message || t('settings.email.errorLoadingTemplates'));
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await settingsService.getEmailLogs({
        page: logPage + 1,
        limit: 10,
        status: logFilters.status || undefined,
        email: logFilters.email || undefined,
      });
      setLogs(response.data || []);
      setLogTotal(response.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error fetching logs:', err);
      setError(err.message || t('settings.email.errorLoadingHistory'));
    }
  }, [logPage, logFilters]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchDashboard(),
          fetchConfigs(),
          fetchSignatures(),
          fetchTemplates(),
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchDashboard, fetchConfigs, fetchSignatures, fetchTemplates]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchLogs();
    }
  }, [activeTab, fetchLogs]);

  // Config handlers
  const handleOpenConfigDialog = (config?: EmailConfig) => {
    if (config) {
      setSelectedConfig(config);
      setConfigForm({
        description: config.description || '',
        smtpHost: config.smtpHost || 'smtp.gmail.com',
        smtpPort: config.smtpPort || 587,
        smtpSecure: config.smtpSecure || false,
        smtpUser: config.smtpUser || '',
        smtpPassword: config.smtpPassword || '',
        fromName: config.fromName || '',
        fromEmail: config.fromEmail || '',
        replyTo: config.replyTo || '',
        environment: (config.environment?.toLowerCase() === 'producao' ? 'producao' : 'sandbox') as Environment,
        active: config.active,
      });
    } else {
      setSelectedConfig(null);
      setConfigForm({
        description: '',
        smtpHost: 'smtp.gmail.com',
        smtpPort: 587,
        smtpSecure: false,
        smtpUser: '',
        smtpPassword: '',
        fromName: '',
        fromEmail: '',
        replyTo: '',
        environment: configEnvTab,
        active: true,
      });
    }
    setShowPassword(false);
    setConfigDialog(true);
  };

  const handleSaveConfig = async () => {
    try {
      setError(null);
      const data = {
        description: configForm.description,
        smtpHost: configForm.smtpHost,
        smtpPort: configForm.smtpPort,
        smtpSecure: configForm.smtpSecure,
        smtpUser: configForm.smtpUser,
        smtpPassword: configForm.smtpPassword,
        fromName: configForm.fromName || undefined,
        fromEmail: configForm.fromEmail,
        replyTo: configForm.replyTo || undefined,
        environment: configForm.environment,
        active: configForm.active,
      };
      if (selectedConfig) {
        await settingsService.updateEmailConfig(selectedConfig.id, data);
        setSuccess(t('settings.email.configUpdated'));
      } else {
        await settingsService.createEmailConfig(data);
        setSuccess(t('settings.email.configCreated'));
      }
      setConfigDialog(false);
      fetchConfigs();
    } catch (err: any) {
      setError(err.message || t('settings.email.errorSavingConfig'));
    }
  };

  const handleDeleteConfig = async (config: EmailConfig) => {
    if (!window.confirm(t('settings.email.confirmDeleteConfig', { name: config.description }))) return;
    try {
      await settingsService.deleteEmailConfig(config.id);
      setSuccess(t('settings.email.configDeleted'));
      fetchConfigs();
    } catch (err: any) {
      setError(err.message || t('settings.email.errorDeleting'));
    }
  };

  const handleTestConfig = async (config: EmailConfig) => {
    try {
      setError(null);
      const result = await settingsService.testEmailConfig(config.id);
      if (result.success) {
        setSuccess(t('settings.email.testSuccess'));
      } else {
        setError(result.message || t('settings.email.testError'));
      }
    } catch (err: any) {
      setError(err.message || t('settings.email.testSmtpError'));
    }
  };

  // Signature handlers
  const handleOpenSignatureDialog = (signature?: EmailSignature) => {
    if (signature) {
      setSelectedSignature(signature);
      setSignatureForm({
        name: signature.name,
        contentHtml: signature.contentHtml,
        active: signature.active,
        isDefault: signature.isDefault,
      });
    } else {
      setSelectedSignature(null);
      setSignatureForm({
        name: '',
        contentHtml: '',
        active: true,
        isDefault: false,
      });
    }
    setSignatureDialog(true);
  };

  const handleSaveSignature = async () => {
    try {
      setError(null);
      const data = {
        name: signatureForm.name,
        contentHtml: signatureForm.contentHtml,
        active: signatureForm.active,
        isDefault: signatureForm.isDefault,
      };
      if (selectedSignature) {
        await settingsService.updateEmailSignature(selectedSignature.id, data);
        setSuccess(t('settings.email.signatureUpdated'));
      } else {
        await settingsService.createEmailSignature(data);
        setSuccess(t('settings.email.signatureCreated'));
      }
      setSignatureDialog(false);
      fetchSignatures();
    } catch (err: any) {
      setError(err.message || t('settings.email.errorSavingSignature'));
    }
  };

  const handleDeleteSignature = async (signature: EmailSignature) => {
    if (!window.confirm(t('settings.email.confirmDeleteSignature', { name: signature.name }))) return;
    try {
      await settingsService.deleteEmailSignature(signature.id);
      setSuccess(t('settings.email.signatureDeleted'));
      fetchSignatures();
    } catch (err: any) {
      setError(err.message || t('settings.email.errorDeleting'));
    }
  };

  // Template handlers
  const handleOpenTemplateDialog = (template?: EmailTemplate) => {
    if (template) {
      setSelectedTemplate(template);
      setTemplateForm({
        code: template.code,
        name: template.name,
        subject: template.subject,
        contentHtml: template.contentHtml,
        contentText: template.contentText || '',
        type: template.type,
        signatureId: template.signatureId || '',
        active: template.active,
      });
    } else {
      setSelectedTemplate(null);
      setTemplateForm({
        code: '',
        name: '',
        subject: '',
        contentHtml: '',
        contentText: '',
        type: 'transacional',
        signatureId: '',
        active: true,
      });
    }
    setTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    try {
      setError(null);
      const data = {
        code: templateForm.code,
        name: templateForm.name,
        subject: templateForm.subject,
        contentHtml: templateForm.contentHtml,
        contentText: templateForm.contentText || undefined,
        type: templateForm.type,
        signatureId: templateForm.signatureId || undefined,
        active: templateForm.active,
      };
      if (selectedTemplate) {
        await settingsService.updateEmailTemplate(selectedTemplate.id, data);
        setSuccess(t('settings.email.templateUpdated'));
      } else {
        await settingsService.createEmailTemplate(data);
        setSuccess(t('settings.email.templateCreated'));
      }
      setTemplateDialog(false);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || t('settings.email.errorSavingTemplate'));
    }
  };

  const handleDeleteTemplate = async (template: EmailTemplate) => {
    if (!window.confirm(t('settings.email.confirmDeleteTemplate', { name: template.name }))) return;
    try {
      await settingsService.deleteEmailTemplate(template.id);
      setSuccess(t('settings.email.templateDeleted'));
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || t('settings.email.errorDeleting'));
    }
  };

  // Send Email handler
  const handleSendEmail = async () => {
    setSending(true);
    try {
      setError(null);
      if (sendForm.templateCode) {
        await settingsService.sendEmailTemplate({
          email: sendForm.email,
          templateCode: sendForm.templateCode,
          variables: Object.keys(sendForm.variables).length > 0 ? sendForm.variables : undefined,
        });
      } else {
        await settingsService.sendEmail({
          email: sendForm.email,
          subject: sendForm.subject,
          contentHtml: sendForm.contentHtml,
        });
      }
      setSuccess(t('settings.email.emailSent'));
      setSendDialog(false);
      setSendForm({ email: '', templateCode: '', subject: '', contentHtml: '', variables: {} });
      fetchDashboard();
      fetchLogs();
    } catch (err: any) {
      setError(err.message || t('settings.email.errorSendingEmail'));
    } finally {
      setSending(false);
    }
  };

  // Status helpers
  const getStatusChip = (status: string) => {
    const statusMap: Record<string, { color: 'success' | 'warning' | 'error' | 'default' | 'info' | 'primary'; icon: React.ReactNode }> = {
      enviado: { color: 'success', icon: <CheckCircle fontSize="small" /> },
      entregue: { color: 'success', icon: <DoneAll fontSize="small" /> },
      pendente: { color: 'warning', icon: <HourglassEmpty fontSize="small" /> },
      falhou: { color: 'error', icon: <ErrorOutline fontSize="small" /> },
      rejeitado: { color: 'error', icon: <Cancel fontSize="small" /> },
      aberto: { color: 'info', icon: <OpenInNew fontSize="small" /> },
      clicado: { color: 'primary', icon: <TouchApp fontSize="small" /> },
    };
    const config = statusMap[status] || { color: 'default' as const, icon: null };
    return <Chip size="small" label={status} color={config.color} icon={config.icon as any} />;
  };

  const getTemplateTypeChip = (type: string) => {
    const typeMap: Record<string, { color: 'primary' | 'secondary' | 'success' | 'warning'; label: string }> = {
      transacional: { color: 'primary', label: t('settings.email.transactional') },
      marketing: { color: 'secondary', label: t('settings.email.marketing') },
      notificacao: { color: 'success', label: t('settings.email.notification') },
      sistema: { color: 'warning', label: t('settings.email.system') },
    };
    const config = typeMap[type] || { color: 'primary' as const, label: type };
    return <Chip size="small" label={config.label} color={config.color} variant="outlined" />;
  };

  // Filter configs by environment
  const filteredConfigs = configs.filter(c => {
    const env = c.environment?.toLowerCase() || 'sandbox';
    return env === configEnvTab;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EmailIcon sx={{ fontSize: 32 }} color="primary" />
          <Typography variant="h5" fontWeight={600}>
            {t('settings.email.title')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => setSendDialog(true)}
          disabled={configs.filter(c => c.active).length === 0}
        >
          {t('settings.email.sendEmail')}
        </Button>
      </Box>

      {/* Main Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="dashboard" icon={<DashboardIcon />} iconPosition="start" label={t('settings.email.dashboard')} />
          <Tab value="config" icon={<ConfigIcon />} iconPosition="start" label={t('settings.email.configs')} />
          <Tab value="signatures" icon={<SignatureIcon />} iconPosition="start" label={t('settings.email.signatures')} />
          <Tab value="templates" icon={<TemplateIcon />} iconPosition="start" label={t('settings.email.templates')} />
          <Tab value="history" icon={<HistoryIcon />} iconPosition="start" label={t('settings.email.history')} />
        </Tabs>
      </Paper>

      {/* ==================== DASHBOARD TAB ==================== */}
      {activeTab === 'dashboard' && (
        <Box>
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="primary" fontSize="large" />
                    <Box>
                      <Typography variant="h4" fontWeight={700}>{stats?.all?.total || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('settings.email.totalEmails')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DoneAll color="success" fontSize="large" />
                    <Box>
                      <Typography variant="h4" fontWeight={700}>{stats?.all?.delivered || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('settings.email.delivered')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OpenInNew color="info" fontSize="large" />
                    <Box>
                      <Typography variant="h4" fontWeight={700}>{stats?.all?.opened || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('settings.email.opened')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorOutline color="error" fontSize="large" />
                    <Box>
                      <Typography variant="h4" fontWeight={700}>{stats?.all?.failed || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('settings.email.failed')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Today & Last 7 Days */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('settings.email.today')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="primary">
                        {stats?.today?.total || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.email.sent')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="success.main">
                        {stats?.today?.delivered || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.email.delivered')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="info.main">
                        {stats?.today?.opened || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.email.opened')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="error.main">
                        {stats?.today?.failed || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.email.failed')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('settings.email.last7Days')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="primary">
                        {stats?.week?.total || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.email.sent')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="success.main">
                        {stats?.week?.delivered || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.email.delivered')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="info.main">
                        {stats?.week?.opened || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.email.opened')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="error.main">
                        {stats?.week?.failed || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.email.failed')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Status */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('settings.email.activeConfigs')}
                  </Typography>
                  <Typography variant="h3" fontWeight={700} color="success.main">
                    {configs.filter(c => c.active).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.email.ofConfigs', { count: configs.length })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('settings.email.activeTemplates')}
                  </Typography>
                  <Typography variant="h3" fontWeight={700} color="primary">
                    {templates.filter(t => t.active).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.email.ofTemplates', { count: templates.length })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('settings.email.activeSignatures')}
                  </Typography>
                  <Typography variant="h3" fontWeight={700} color="info.main">
                    {signatures.filter(s => s.active).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.email.ofSignatures', { count: signatures.length })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Activity */}
          <Paper sx={{ mt: 3 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('settings.email.recentActivity')}
              </Typography>
              <IconButton size="small" onClick={fetchDashboard}>
                <RefreshIcon />
              </IconButton>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.email')}</TableCell>
                    <TableCell>{t('settings.email.subject')}</TableCell>
                    <TableCell>{t('common.status')}</TableCell>
                    <TableCell>{t('common.date')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">{t('settings.email.noEmailSent')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {log.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={log.subject}>
                            <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {log.subject}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{getStatusChip(log.status)}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {log.sentAt ? new Date(log.sentAt).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* ==================== CONFIG TAB ==================== */}
      {activeTab === 'config' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('settings.email.smtpConfigs')}</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenConfigDialog()}>
              {t('settings.email.newConfig')}
            </Button>
          </Box>

          {/* Environment Tabs */}
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={configEnvTab}
              onChange={(_, newValue) => setConfigEnvTab(newValue)}
            >
              <Tab
                value="producao"
                icon={<ProductionIcon />}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('settings.posSisp.production')}
                    <Chip size="small" label={configs.filter(c => c.environment?.toLowerCase() === 'producao').length} color="success" />
                  </Box>
                }
              />
              <Tab
                value="sandbox"
                icon={<SandboxIcon />}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('settings.posSisp.sandbox')}
                    <Chip size="small" label={configs.filter(c => c.environment?.toLowerCase() === 'sandbox').length} color="info" />
                  </Box>
                }
              />
            </Tabs>
          </Paper>

          <Alert severity={configEnvTab === 'sandbox' ? 'info' : 'warning'} sx={{ mb: 2 }}>
            {configEnvTab === 'sandbox'
              ? t('settings.email.sandboxEmailAlert')
              : t('settings.email.productionEmailAlert')
            }
          </Alert>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.description')}</TableCell>
                    <TableCell>{t('settings.email.smtpHost')}</TableCell>
                    <TableCell>{t('settings.email.fromEmail')}</TableCell>
                    <TableCell align="center">{t('common.status')}</TableCell>
                    <TableCell align="center" width={150}>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredConfigs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">{t('settings.email.noConfig')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredConfigs.slice(configPage * 10, configPage * 10 + 10).map((config) => (
                      <TableRow key={config.id} hover>
                        <TableCell>
                          <Typography fontWeight={600}>{config.description}</Typography>
                          <Typography variant="caption" color="text.secondary">{config.fromName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontFamily="monospace" variant="body2">
                            {config.smtpHost}:{config.smtpPort}
                          </Typography>
                        </TableCell>
                        <TableCell>{config.fromEmail}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={config.active ? t('common.active') : t('common.inactive')}
                            color={config.active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={t('settings.email.testConnection')}>
                            <IconButton size="small" onClick={() => handleTestConfig(config)} color="info">
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" onClick={() => handleOpenConfigDialog(config)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteConfig(config)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredConfigs.length > 10 && (
              <TablePagination
                component="div"
                count={filteredConfigs.length}
                page={configPage}
                onPageChange={(_, p) => setConfigPage(p)}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
              />
            )}
          </Paper>
        </Box>
      )}

      {/* ==================== SIGNATURES TAB ==================== */}
      {activeTab === 'signatures' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('settings.email.emailSignatures')}</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenSignatureDialog()}>
              {t('settings.email.newSignature')}
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('settings.email.signaturesInfo')}
          </Alert>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.name')}</TableCell>
                    <TableCell>{t('settings.email.preview')}</TableCell>
                    <TableCell align="center">{t('settings.email.default')}</TableCell>
                    <TableCell align="center">{t('common.status')}</TableCell>
                    <TableCell align="center" width={120}>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {signatures.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">{t('settings.email.noSignature')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    signatures.slice(signaturePage * 10, signaturePage * 10 + 10).map((signature) => (
                      <TableRow key={signature.id} hover>
                        <TableCell>
                          <Typography fontWeight={600}>{signature.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={<div dangerouslySetInnerHTML={{ __html: signature.contentHtml }} />}>
                            <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {signature.contentHtml.replace(/<[^>]*>/g, '').substring(0, 100)}...
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          {signature.isDefault ? (
                            <StarIcon color="warning" />
                          ) : (
                            <StarBorderIcon color="disabled" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={signature.active ? t('common.active') : t('common.inactive')}
                            color={signature.active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleOpenSignatureDialog(signature)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteSignature(signature)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {signatures.length > 10 && (
              <TablePagination
                component="div"
                count={signatures.length}
                page={signaturePage}
                onPageChange={(_, p) => setSignaturePage(p)}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
              />
            )}
          </Paper>
        </Box>
      )}

      {/* ==================== TEMPLATES TAB ==================== */}
      {activeTab === 'templates' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('settings.email.emailTemplates')}</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenTemplateDialog()}>
              {t('settings.email.newTemplate')}
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('settings.email.templatesInfo')}
          </Alert>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.code')}</TableCell>
                    <TableCell>{t('common.name')}</TableCell>
                    <TableCell>{t('settings.email.subject')}</TableCell>
                    <TableCell>{t('common.type')}</TableCell>
                    <TableCell align="center">{t('common.status')}</TableCell>
                    <TableCell align="center" width={120}>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">{t('settings.email.noTemplate')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    templates.slice(templatePage * 10, templatePage * 10 + 10).map((template) => (
                      <TableRow key={template.id} hover>
                        <TableCell>
                          <Typography fontFamily="monospace" fontWeight={600} variant="body2">
                            {template.code}
                          </Typography>
                        </TableCell>
                        <TableCell>{template.name}</TableCell>
                        <TableCell>
                          <Tooltip title={template.subject}>
                            <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {template.subject}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{getTemplateTypeChip(template.type)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={template.active ? t('common.active') : t('common.inactive')}
                            color={template.active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleOpenTemplateDialog(template)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(template)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {templates.length > 10 && (
              <TablePagination
                component="div"
                count={templates.length}
                page={templatePage}
                onPageChange={(_, p) => setTemplatePage(p)}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
              />
            )}
          </Paper>
        </Box>
      )}

      {/* ==================== HISTORY TAB ==================== */}
      {activeTab === 'history' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('settings.email.sendHistory')}</Typography>
            <IconButton onClick={fetchLogs}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label={t('common.status')}
                  value={logFilters.status}
                  onChange={(e) => {
                    setLogFilters({ ...logFilters, status: e.target.value });
                    setLogPage(0);
                  }}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  <MenuItem value="pendente">Pendente</MenuItem>
                  <MenuItem value="enviado">Enviado</MenuItem>
                  <MenuItem value="entregue">Entregue</MenuItem>
                  <MenuItem value="falhou">Falhou</MenuItem>
                  <MenuItem value="rejeitado">Rejeitado</MenuItem>
                  <MenuItem value="aberto">Aberto</MenuItem>
                  <MenuItem value="clicado">Clicado</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('common.email')}
                  value={logFilters.email}
                  onChange={(e) => setLogFilters({ ...logFilters, email: e.target.value })}
                  placeholder="email@exemplo.com"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setLogFilters({ status: '', email: '' });
                    setLogPage(0);
                  }}
                >
                  {t('settings.email.clearFilters')}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.email')}</TableCell>
                    <TableCell>{t('settings.email.subject')}</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell align="center">{t('common.status')}</TableCell>
                    <TableCell>{t('settings.email.sentDate')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">{t('settings.email.noEmailFound')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Typography variant="body2">{log.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={log.subject}>
                            <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {log.subject}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{log.templateName || '-'}</TableCell>
                        <TableCell align="center">{getStatusChip(log.status)}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {log.sentAt ? new Date(log.sentAt).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={logTotal}
              page={logPage}
              onPageChange={(_, p) => setLogPage(p)}
              rowsPerPage={10}
              rowsPerPageOptions={[10]}
              labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
            />
          </Paper>
        </Box>
      )}

      {/* ==================== DIALOGS ==================== */}

      {/* Config Dialog */}
      <Dialog open={configDialog} onClose={() => setConfigDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {configForm.environment === 'sandbox' ? <SandboxIcon color="info" /> : <ProductionIcon color="success" />}
          {selectedConfig ? t('settings.email.editSmtpConfig') : t('settings.email.newSmtpConfig')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label={t('common.description')}
                value={configForm.description}
                onChange={(e) => setConfigForm({ ...configForm, description: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={configForm.environment}
                onChange={(e) => setConfigForm({ ...configForm, environment: e.target.value as Environment })}
              >
                <MenuItem value="sandbox">{t('settings.posSisp.sandbox')}</MenuItem>
                <MenuItem value="producao">{t('settings.posSisp.production')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('settings.email.smtpHost')}
                value={configForm.smtpHost}
                onChange={(e) => setConfigForm({ ...configForm, smtpHost: e.target.value })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="number"
                label={t('settings.email.smtpPort')}
                value={configForm.smtpPort}
                onChange={(e) => setConfigForm({ ...configForm, smtpPort: parseInt(e.target.value) || 587 })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={configForm.smtpSecure}
                    onChange={(e) => setConfigForm({ ...configForm, smtpSecure: e.target.checked })}
                  />
                }
                label="SSL/TLS"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('settings.email.smtpUser')}
                value={configForm.smtpUser}
                onChange={(e) => setConfigForm({ ...configForm, smtpUser: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('settings.email.smtpPassword')}
                value={configForm.smtpPassword}
                onChange={(e) => setConfigForm({ ...configForm, smtpPassword: e.target.value })}
                type={showPassword ? 'text' : 'password'}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('settings.email.senderName')}
                value={configForm.fromName}
                onChange={(e) => setConfigForm({ ...configForm, fromName: e.target.value })}
                placeholder="Lyra - Pagali"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('settings.email.senderEmail')}
                value={configForm.fromEmail}
                onChange={(e) => setConfigForm({ ...configForm, fromEmail: e.target.value })}
                placeholder="lyra@pagali.ai"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.email.replyTo')}
                value={configForm.replyTo}
                onChange={(e) => setConfigForm({ ...configForm, replyTo: e.target.value })}
                placeholder="suporte@pagali.cv"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={configForm.active}
                    onChange={(e) => setConfigForm({ ...configForm, active: e.target.checked })}
                    color="success"
                  />
                }
                label={configForm.active ? t('common.active') : t('common.inactive')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveConfig}
            disabled={!configForm.description || !configForm.smtpHost || !configForm.smtpUser || !configForm.smtpPassword || !configForm.fromEmail}
          >
            {selectedConfig ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={signatureDialog} onClose={() => setSignatureDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedSignature ? t('settings.email.editSignature') : t('settings.email.newSignature')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label={t('common.name')}
                value={signatureForm.name}
                onChange={(e) => setSignatureForm({ ...signatureForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={signatureForm.isDefault}
                      onChange={(e) => setSignatureForm({ ...signatureForm, isDefault: e.target.checked })}
                      color="warning"
                    />
                  }
                  label={t('settings.email.default')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={signatureForm.active}
                      onChange={(e) => setSignatureForm({ ...signatureForm, active: e.target.checked })}
                      color="success"
                    />
                  }
                  label="Activo"
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.email.htmlContent')}
                value={signatureForm.contentHtml}
                onChange={(e) => setSignatureForm({ ...signatureForm, contentHtml: e.target.value })}
                multiline
                rows={10}
                required
                helperText={t('settings.email.htmlHelper')}
              />
            </Grid>
            {signatureForm.contentHtml && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>{t('settings.email.preview')}:</Typography>
                <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                  <div dangerouslySetInnerHTML={{ __html: signatureForm.contentHtml }} />
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignatureDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveSignature}
            disabled={!signatureForm.name || !signatureForm.contentHtml}
          >
            {selectedSignature ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialog} onClose={() => setTemplateDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedTemplate ? t('settings.email.editTemplate') : t('settings.email.newTemplate')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t('common.code')}
                value={templateForm.code}
                onChange={(e) => setTemplateForm({ ...templateForm, code: e.target.value.toUpperCase() })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
                disabled={!!selectedTemplate}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('common.type')}
                value={templateForm.type}
                onChange={(e) => setTemplateForm({ ...templateForm, type: e.target.value as any })}
              >
                <MenuItem value="transacional">{t('settings.email.transactional')}</MenuItem>
                <MenuItem value="marketing">{t('settings.email.marketing')}</MenuItem>
                <MenuItem value="notificacao">{t('settings.email.notification')}</MenuItem>
                <MenuItem value="sistema">{t('settings.email.system')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('settings.email.signatureLabel')}
                value={templateForm.signatureId}
                onChange={(e) => setTemplateForm({ ...templateForm, signatureId: e.target.value })}
              >
                <MenuItem value="">{t('settings.email.noSignatureOption')}</MenuItem>
                {signatures.filter(s => s.active).map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('common.name')}
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.email.subject')}
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                required
                helperText={t('settings.email.subjectHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.email.htmlContent')}
                value={templateForm.contentHtml}
                onChange={(e) => setTemplateForm({ ...templateForm, contentHtml: e.target.value })}
                multiline
                rows={8}
                required
                helperText={t('settings.email.subjectHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.email.textContent')}
                value={templateForm.contentText}
                onChange={(e) => setTemplateForm({ ...templateForm, contentText: e.target.value })}
                multiline
                rows={4}
                helperText={t('settings.email.textContentHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={templateForm.active}
                    onChange={(e) => setTemplateForm({ ...templateForm, active: e.target.checked })}
                    color="success"
                  />
                }
                label={templateForm.active ? t('common.active') : t('common.inactive')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={!templateForm.code || !templateForm.name || !templateForm.subject || !templateForm.contentHtml}
          >
            {selectedTemplate ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={sendDialog} onClose={() => setSendDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon color="primary" />
          {t('settings.email.sendEmail')}
        </DialogTitle>
        <DialogContent dividers>
          {sending && <LinearProgress sx={{ mb: 2 }} />}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.email.emailRecipient')}
                value={sendForm.email}
                onChange={(e) => setSendForm({ ...sendForm, email: e.target.value })}
                placeholder="email@exemplo.com"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label={t('settings.email.useTemplate')}
                value={sendForm.templateCode}
                onChange={(e) => setSendForm({ ...sendForm, templateCode: e.target.value })}
                helperText={t('settings.email.selectTemplateHelper')}
              >
                <MenuItem value="">{t('settings.email.customMessage')}</MenuItem>
                {templates.filter(t => t.active).map((t) => (
                  <MenuItem key={t.id} value={t.code}>{t.name} - {t.subject}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {!sendForm.templateCode && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label={t('settings.email.subject')}
                    value={sendForm.subject}
                    onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label={t('settings.email.htmlContent')}
                    value={sendForm.contentHtml}
                    onChange={(e) => setSendForm({ ...sendForm, contentHtml: e.target.value })}
                    multiline
                    rows={6}
                    required
                  />
                </Grid>
              </>
            )}
            {sendForm.templateCode && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>{t('settings.email.templateSelected')}</strong>{' '}
                    {templates.find(t => t.code === sendForm.templateCode)?.name}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Assunto: {templates.find(t => t.code === sendForm.templateCode)?.subject}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialog(false)} disabled={sending}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={!sendForm.email || (!sendForm.templateCode && (!sendForm.subject || !sendForm.contentHtml)) || sending}
            startIcon={<SendIcon />}
          >
            {sending ? t('settings.email.sending') : t('settings.email.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailPage;
